// API client for Kyoto's /api/copilot/v1/* endpoints.
// CSRF: reads csrftoken cookie; bootstrapped via GET /csrf before first POST.
// Per spec § 6.1 and Cross-cutting § A.
//
// Kyoto is a single deployment per environment (copilot-<env>.<DOMAIN>, or bare
// copilot.<DOMAIN> in prod), distinct from Denver, which is served per release
// channel (app[-<env>][-alpha|-beta]). The channel is conveyed to kyoto via the
// Origin header — there is NO per-channel copilot host — so every denver channel
// in an env resolves to that env's single copilot host. The base comes from
// REACT_APP_KYOTO_BASE in dev/electron; otherwise it's derived from the hostname.

import { MAX_LONG_EDGE } from "./coordinateScaling";

/**
 * Derive kyoto's base URL from denver's own host. Strips the app-/dash- prefix
 * and any -alpha/-beta release-channel suffix, keeping the env infix:
 *   app-alpha.v2cloud.com         -> https://copilot.v2cloud.com         (prod)
 *   app-qa-alpha.hazeware.com     -> https://copilot-qa.hazeware.com     (qa)
 *   app-devel00-beta.hazeware.com -> https://copilot-devel00.hazeware.com (dev)
 * Returns "" for hosts without a domain (localhost/electron) so the caller fails
 * loudly rather than silently posting to the wrong origin.
 * @param {string} host - e.g. window.location.hostname
 * @param {string} protocol - e.g. window.location.protocol ("https:")
 * @returns {string}
 */
export function deriveKyotoBase(host, protocol) {
  if (!host) return "";
  const dot = host.indexOf(".");
  if (dot === -1) return "";
  const sub = host.slice(0, dot);
  const domain = host.slice(dot + 1);
  const m = sub.match(/^(?:app|dash)(.*)$/);
  if (!m) return "";
  const env = m[1].replace(/-(?:alpha|beta)$/, "");
  return `${protocol}//copilot${env}.${domain}`;
}

const KYOTO_BASE =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_KYOTO_BASE) ||
    (typeof window !== "undefined"
      ? deriveKyotoBase(window.location.hostname, window.location.protocol)
      : "");
export const BASE = `${KYOTO_BASE}/api/copilot/v1`;

/**
 * Read Kyoto's CSRF token from the browser cookie jar.
 * Kyoto uses `copilot_csrftoken` (not Django's default `csrftoken`) to avoid
 * collision with Tokyo's cookie on the shared parent domain — see
 * kyoto/kyoto/configuration.py CSRF_COOKIE_NAME.
 * @returns {string}
 */
export function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)copilot_csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

/**
 * The app's live OAuth bearer header ("Bearer <token>"), kept on window.axios by
 * auth.js. Kyoto's bearer-auth path uses it to resolve request.user — required
 * for Denver-desktop, which has no shared Django session cookie. Returns {} when
 * no token is set (the browser then falls back to its session cookie).
 * @returns {{ Authorization?: string }}
 */
export function authHeader() {
  const t =
    (typeof window !== "undefined" &&
      window.axios?.defaults?.headers?.common?.Authorization) || "";
  return t ? { Authorization: t } : {};
}

/**
 * Trigger a GET /csrf to ensure Django sets the csrftoken cookie cross-domain.
 * Call once at CopilotController construction before any POST.
 * Per Cross-cutting § A: Kyoto exposes @ensure_csrf_cookie on this endpoint.
 */
export async function ensureCsrf() {
  await fetch(`${BASE}/csrf`, { credentials: "include" });
}

/**
 * Generic JSON request to Kyoto. Attaches X-CSRFToken on mutating methods.
 *
 * @param {string} path - Path relative to BASE.
 * @param {RequestInit} opts
 * @returns {Promise<{ status: number, body: any }>}
 */
async function jsonReq(path, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = {
    "Content-Type": "application/json",
    ...authHeader(),
    ...(opts.headers || {}),
  };
  if (method !== "GET" && method !== "HEAD") {
    headers["X-CSRFToken"] = csrfToken();
  }
  const res = await fetch(BASE + path, {
    credentials: "include",
    headers,
    ...opts,
  });
  // Parse JSON for success AND for every error shape. Enumerating statuses
  // (ok/402/429/403) meant any status left off the list silently dropped its
  // body — that is how kyoto's `{"error": "no_grant"}` got lost and every 403
  // degraded to a bare `http_403`. WorkflowLibrary's `no_access` had the same
  // latent bug on other statuses. The parse is already guarded, so attempting
  // it unconditionally costs nothing and can't regress the same way twice.
  let body = null;
  try { body = await res.json(); } catch (_) {}
  return { status: res.status, body };
}

/**
 * Kyoto grant/setup failures — the admin must enable Copilot for this user.
 *
 * Mirrors kyoto's SETUP_REQUIRED_ERRORS. Kyoto annotates these refusals with
 * `setup_required`, and CopilotButton prefers that flag because it also says
 * whether the user is *managed* (i.e. whether the setup page is reachable for
 * them at all). This list is the fallback for version skew — a denver deployed
 * ahead of kyoto still gets the right message, just without the setup link.
 */
const ACCESS_DENIED_CODES = new Set([
  "no_grant",
  "profile_not_ready",
  "slot_not_allowed",
]);

export const ACCESS_DENIED_MESSAGE =
  "You don't have access to Copilot. Contact your admin or manager to grant you access.";

export const GENERIC_START_MESSAGE =
  "Copilot couldn't start. Please try again in a moment.";

/**
 * User-facing copy for a failed session start / options fetch.
 * @param {{ code?: string }|null|undefined} err
 * @returns {string}
 */
export function sessionStartErrorMessage(err) {
  if (err && ACCESS_DENIED_CODES.has(err.code)) return ACCESS_DENIED_MESSAGE;
  return GENERIC_START_MESSAGE;
}

function throwKyotoError(r, prefix) {
  const code = r.body?.error || `http_${r.status}`;
  const err = new Error(`${prefix}: ${code}`);
  err.code = code;
  err.status = r.status;
  // Kyoto annotates the not-configured refusals (`no_grant`,
  // `profile_not_ready`) with these. They distinguish "nobody has set Copilot
  // up yet" — which the account's admin can fix, and a managed end-user
  // cannot — from a genuine failure, so the UI can route each to the right
  // place instead of showing one message to everybody.
  err.setupRequired = r.body?.setup_required === true;
  err.managed = r.body?.managed === true;
  throw err;
}

/**
 * Derive tokyo's dashboard base URL from denver's own host — same transform as
 * deriveKyotoBase, targeting `dash` instead of `copilot`:
 *   app-alpha.v2cloud.com         -> https://dash.v2cloud.com          (prod)
 *   app-qa-alpha.hazeware.com     -> https://dash-qa.hazeware.com      (qa)
 *   app-devel00-beta.hazeware.com -> https://dash-devel00.hazeware.com (dev)
 *
 * The `-alpha`/`-beta` strip matters: those are release channels of denver and
 * the dashboard, and there is no dash-alpha/dash-beta host — hardcoding
 * dash.v2cloud.com would send every non-prod user to production instead.
 * Returns "" for hosts without a domain (localhost/electron), so callers fall
 * back to prose rather than rendering a broken link.
 */
export function deriveDashBase(host, protocol) {
  if (!host) return "";
  const dot = host.indexOf(".");
  if (dot === -1) return "";
  const sub = host.slice(0, dot);
  const domain = host.slice(dot + 1);
  const m = sub.match(/^(?:app|dash)(.*)$/);
  if (!m) return "";
  const env = m[1].replace(/-(?:alpha|beta)$/, "");
  return `${protocol}//dash${env}.${domain}`;
}

/**
 * Where an admin goes to configure Copilot (keys, profiles, grants).
 * Empty string when the host can't be derived — the caller must handle that.
 */
export function copilotSetupUrl() {
  const base =
    typeof window !== "undefined"
      ? deriveDashBase(window.location.hostname, window.location.protocol)
      : "";
  return base ? `${base}/copilot/?tab=setup` : "";
}

/**
 * Read the caller's selectable profile slots.
 * GET /v1/sessions/options — returns the grant's default + allowed slots.
 *
 * The grant is per-user (not per-VM), so this needs no parameters. Denver
 * calls this once at panel-open to populate the profile picker and pick a
 * default slot for the first session.
 *
 * @returns {Promise<{ defaultSlot: string, allowedSlots: string[] }>}
 * @throws {Error} if the user has no enabled grant (HTTP 403 no_grant).
 */
export async function getSessionOptions() {
  const r = await jsonReq("/sessions/options");
  if (r.status !== 200) throwKyotoError(r, "getSessionOptions failed");
  return {
    defaultSlot: r.body.default_profile_slot,
    allowedSlots: r.body.allowed_profile_slots || [],
  };
}

/**
 * Start a new Copilot session.
 * POST /v1/sessions — per spec § 6.1.
 *
 * @param {{ permissionId: number|string, profileSlot: string }} params
 *   permissionId — id of the cloud_permission row representing the active
 *   connection (Denver's `active_perm`). Kyoto verifies ownership and
 *   resolves it to a vm_id internally.
 * @returns {Promise<{ sessionId: string, maxLongEdge: number }>}
 *   maxLongEdge is the model-appropriate long-edge cap (§12), defaulting to
 *   MAX_LONG_EDGE when the server omits recommended_max_long_edge.
 */
export async function startSession({ permissionId, profileSlot, workflowId }) {
  const payload = { permission_id: permissionId, profile_slot: profileSlot };
  if (workflowId !== undefined && workflowId !== null) {
    payload.workflow_id = workflowId;
  }
  const r = await jsonReq("/sessions/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (r.status !== 200) throwKyotoError(r, "startSession failed");
  return {
    sessionId: r.body.session_id,
    maxLongEdge: r.body.recommended_max_long_edge ?? MAX_LONG_EDGE,
  };
}

/**
 * Post a turn. v2 multipart wire protocol per spec § 6.1.
 * do NOT set Content-Type — browser sets it with the multipart boundary.
 *
 * @param {string} sessionId
 * @param {object} params
 * @param {string} params.turnRequestId - UUID (crypto.randomUUID()). Reused on retry.
 * @param {Blob|null} params.screenshotBlob - current_screenshot, or null when
 *   the model did not ask for a fresh capture this iteration (Tier 1.b § 4.3).
 *   When null, the multipart field is omitted entirely — kyoto reads bare
 *   absence as the screenshot-omitted signal.
 * @param {{width: number, height: number}} params.dimensions
 * @param {Blob|null} params.previousScreenshotBlob - prior turn's screenshot, null on first turn.
 * @param {Array|null} params.toolResults - [{id, text?, error?, image_data_url?}, ...] or null.
 *   image_data_url is stripped here — it is browser-memory-only state used to
 *   reconstruct the next POST's current_screenshot and to display what the
 *   agent saw. It must not ride inside tool_results JSON.
 * @param {string|null} params.userMessage - only on first iteration of an ask().
 * @returns {Promise<{ status: number, body: any }>}
 */
export async function postTurn(sessionId, {
  turnRequestId,
  screenshotBlob,
  dimensions,
  previousScreenshotBlob,
  toolResults,
  userMessage,
  signal,
}) {
  const fd = new FormData();
  fd.append("turn_request_id", turnRequestId);
  if (screenshotBlob) {
    fd.append("current_screenshot", screenshotBlob, "screenshot.jpg");
  }
  fd.append("screenshot_dimensions", JSON.stringify(dimensions));
  if (previousScreenshotBlob) {
    fd.append("previous_screenshot", previousScreenshotBlob, "prev.jpg");
  }
  if (toolResults != null) {
    // image_data_url is browser-memory-only state and is stripped — EXCEPT for
    // zoom results, whose crop is the model's requested observation and must
    // ride to kyoto as the tool_result image.
    const stripped = toolResults.map((r) =>
      r.type === "zoom" ? r : (({ image_data_url, ...rest }) => rest)(r));
    fd.append("tool_results", JSON.stringify(stripped));
  }
  if (userMessage != null) {
    fd.append("user_message", userMessage);
  }

  const res = await fetch(`${BASE}/sessions/${sessionId}/turn`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRFToken": csrfToken(), ...authHeader() },
    body: fd,
    signal,
  });
  let body = null;
  try { body = await res.json(); } catch (_) {}
  return { status: res.status, body };
}

/**
 * End a session.
 * POST /v1/sessions/{id}/end — per spec § 6.1; reason set per
 * specs/2026-05-15-copilot-connection-pinning-design.md § 5.
 *
 * When `reason` is omitted, no body is sent — kyoto defaults to `user_stopped`.
 * When provided, must be one of kyoto's client-callable reasons
 * (`user_stopped`, `user_navigated_away`, `vm_disconnected`, `connection_closed`);
 * server-only reasons like `superseded` are rejected by kyoto with HTTP 400.
 *
 * @param {string} sessionId
 * @param {{ reason?: string }} [options]
 * @returns {Promise<{ status: number, body: any }>}
 */
export async function endSession(sessionId, { reason } = {}) {
  const opts = { method: "POST" };
  if (reason) {
    opts.body = JSON.stringify({ end_reason: reason });
  }
  return jsonReq(`/sessions/${sessionId}/end`, opts);
}

/**
 * Get session metadata and turn history (for chat panel rehydration on load).
 * GET /v1/sessions/{id} — per spec § 6.1.
 *
 * @param {string} sessionId
 * @returns {Promise<{ status: number, body: any }>}
 */
export async function getSession(sessionId) {
  return jsonReq(`/sessions/${sessionId}`);
}
